import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { DoctorService } from "app/modules/masters/doctor/doctor.service";
import * as DoctorActions from "./doctor.actions";
import { catchError, map, mergeMap, of } from "rxjs";
import { Doctor } from "app/modules/masters/doctor/models/doctor.model";

@Injectable()
export class DoctorEffects {
  constructor(
    private actions$: Actions,
    private service: DoctorService
  ) {}

  loadDoctors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorActions.loadDoctors),
      mergeMap(({ skip, take, search }) =>{
        // Call the service to fetch doctors with the provided parameters
        return this.service.getAll((skip ?? 0), (take ?? 10), (search ?? "")).pipe(
          map((res) => DoctorActions.loadDoctorsSuccess({ doctors: res.data })),
          catchError((error) => of(DoctorActions.loadDoctorsFailure({ error })))
        )
      }
      )
    )
  );

  createDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorActions.createDoctor),
      mergeMap(({ doctor }) =>
        this.service
          .create(doctor)
          .pipe(
            map((created) =>
              DoctorActions.createDoctorSuccess({ doctor: created })
            )
          )
      )
    )
  );

  updateDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorActions.updateDoctor),
      mergeMap(({ id, doctor }) =>
        this.service
          .update(id, doctor)
          .pipe(
            map((updated) =>
              DoctorActions.updateDoctorSuccess({ doctor: updated })
            )
          )
      )
    )
  );

  deleteDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorActions.deleteDoctor),
      mergeMap(({ id }) =>
        this.service
          .delete(id)
          .pipe(map(() => DoctorActions.deleteDoctorSuccess({ id })))
      )
    )
  );

  checkAltCodeDuplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorActions.checkDoctorAltCodeDuplicate),
      mergeMap(({ altCode, excludeId }) =>
        this.service.checkAltCodeDuplicate(altCode, excludeId).pipe(
          map((res) =>
            DoctorActions.checkDoctorAltCodeDuplicateSuccess({
              exists: res.exists,
            })
          ),
          catchError((error) =>
            of(DoctorActions.checkDoctorAltCodeDuplicateFailure({ error }))
          )
        )
      )
    )
  );

  checkRegistrationDuplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorActions.checkDoctorRegistrationDuplicate),
      mergeMap(({ registrationNumber, excludeId }) =>
        this.service
          .checkRegistrationDuplicate(registrationNumber, excludeId)
          .pipe(
            map((res) =>
              DoctorActions.checkDoctorRegistrationDuplicateSuccess({
                exists: res.exists,
              })
            ),
            catchError((error) =>
              of(
                DoctorActions.checkDoctorRegistrationDuplicateFailure({ error })
              )
            )
          )
      )
    )
  );

  searchDoctors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DoctorActions.searchDoctors),
      mergeMap(({ query, skip = 0, take = 10, rowIndex }) =>
        this.service.search(query, skip, take).pipe(
          map((res: { data: Doctor[]; total: number }) =>
            DoctorActions.searchDoctorsSuccess({
              doctors: res.data,
              total: res.total,
              rowIndex,
            })
          ),

          catchError((error) =>
            of(DoctorActions.searchDoctorsFailure({ error }))
          )
        )
      )
    )
  );
}
